/**
 * Normalize erroneous line/paragraph breaks inside quoted dialogue.
 *
 * Fiction convention: a speech stays one paragraph unless it is long enough to
 * warrant a multi-paragraph quotation (opening quote on each paragraph, closing
 * quote only on the last). Soft mid-speech breaks under that length are merged.
 */

/** Minimum words in the open speech before a multi-paragraph break is kept. */
export const DIALOGUE_PARAGRAPH_MIN_WORDS = 50;

function countWords(text: string): number {
  const matches = text.trim().match(/[A-Za-z0-9\u2019']+/g);
  return matches ? matches.length : 0;
}

function startsWithOpeningQuote(text: string): boolean {
  return /^[\u201C"]/.test(text.trimStart());
}

function endsWithSentenceBoundary(text: string): boolean {
  return /[.!?…]["'\u201D\u2019]?\s*$/u.test(text.trimEnd());
}

/** Walk double quotes (straight + curly) and report whether `text` ends mid-speech. */
export function endsInsideDialogue(text: string): boolean {
  let depth = 0;
  for (const ch of text) {
    if (ch === "\u201C") {
      depth += 1;
    } else if (ch === "\u201D") {
      depth = Math.max(0, depth - 1);
    } else if (ch === '"') {
      depth = depth > 0 ? depth - 1 : depth + 1;
    }
  }
  return depth > 0;
}

/** Word count from the last unclosed opening quote to the end of `text`. */
export function openDialogueWordCount(text: string): number {
  let depth = 0;
  let openAt = -1;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === "\u201C") {
      if (depth === 0) openAt = i;
      depth += 1;
    } else if (ch === "\u201D") {
      depth = Math.max(0, depth - 1);
      if (depth === 0) openAt = -1;
    } else if (ch === '"') {
      if (depth > 0) {
        depth -= 1;
        if (depth === 0) openAt = -1;
      } else {
        openAt = i;
        depth += 1;
      }
    }
  }
  if (openAt < 0 || depth === 0) return 0;
  return countWords(text.slice(openAt + 1));
}

/**
 * True when consecutive paragraphs should be joined: previous ends mid-quote and
 * the break is not an intentional long multi-paragraph speech.
 */
export function shouldMergeDialogueParagraphs(prev: string, next: string): boolean {
  if (!endsInsideDialogue(prev)) return false;
  const intentionalLongSpeech =
    openDialogueWordCount(prev) >= DIALOGUE_PARAGRAPH_MIN_WORDS &&
    endsWithSentenceBoundary(prev) &&
    startsWithOpeningQuote(next);
  return !intentionalLongSpeech;
}

function joinDialogueFragments(prev: string, next: string): string {
  const left = prev.trim();
  let right = next.trim();
  // Erroneous re-open while still inside the same speech — drop the extra opener.
  if (endsInsideDialogue(left) && startsWithOpeningQuote(right)) {
    right = right.replace(/^[\u201C"]\s*/, "");
  }
  if (/[—–-]\s*$/u.test(left) || /^[—–-]/u.test(right)) {
    return `${left.replace(/\s+$/u, "")}${right.replace(/^\s+/u, "")}`;
  }
  return `${left} ${right}`;
}

function collapseInnerNewlines(paragraph: string): string {
  return paragraph.replace(/\n+/g, " ").replace(/[ \t]{2,}/g, " ").trim();
}

function mergeParagraphList(paragraphs: string[]): string[] {
  const out: string[] = [];
  for (const para of paragraphs) {
    if (!para) continue;
    if (out.length === 0) {
      out.push(para);
      continue;
    }
    const prev = out[out.length - 1];
    if (shouldMergeDialogueParagraphs(prev, para)) {
      out[out.length - 1] = joinDialogueFragments(prev, para);
    } else {
      out.push(para);
    }
  }
  return out;
}

/**
 * Merge mid-dialogue paragraph/line breaks. Preserves intentional multi-paragraph
 * quotations when the open speech already has at least {@link DIALOGUE_PARAGRAPH_MIN_WORDS}
 * words, ends on a sentence boundary, and the next paragraph re-opens with a quote.
 */
export function normalizeDialogueParagraphBreaks(text: string): string {
  const normalized = String(text ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\u00A0/g, " ")
    .trim();
  if (!normalized) return "";

  const hasBlankLines = /\n{2,}/.test(normalized);
  const rawParts = hasBlankLines
    ? normalized.split(/\n{2,}/)
    : normalized.split(/\n/);

  const paragraphs = mergeParagraphList(rawParts.map(collapseInnerNewlines).filter(Boolean));
  return paragraphs.join("\n\n");
}
